import {Injectable} from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  DocumentData,
  Firestore,
  FirestoreDataConverter,
  getDoc,
  limit,
  onSnapshot,
  query,
  QueryDocumentSnapshot,
  setDoc,
} from '@angular/fire/firestore';
import {shareReplay} from 'rxjs/operators';
import {Observable} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CollectionAdminCloudService {

  constructor(private firestore: Firestore) {
  }

  // ====================================================================================================
  // Emailer
  // ====================================================================================================

  async setDocument<DataType>(
    path: string,
    data: DataType,
  ): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await setDoc(docRef, data, {merge: true});
    } catch (error) {
      console.error(error);
    }
  }

  async getDocument<DataType>(
    path: string,
  ): Promise<DataType> {
    try {
      const docRef = doc(this.firestore, path);
      const docSnapshot = await getDoc(docRef);
      if (docSnapshot.exists()) {
        return docSnapshot.data() as DataType;
      } else {
        return undefined;
      }
    } catch (error) {
      console.error(error);
    }
  }

  async deleteDocument<DataType>(
    path: string,
  ): Promise<void> {
    try {
      const docRef = doc(this.firestore, path);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting document at path: ${path}\n`, error);
    }
  }

  watchCollection<DataType extends object>(
    collectionPath: string,
    options?: { idField?: string },
  ): Observable<(DataType & Record<string, string>) | undefined> {
    const idField = options?.idField ?? 'id';
    const passthroughConverter = <T extends object>(): FirestoreDataConverter<T> => ({
      toFirestore: (d: T): DocumentData => d as DocumentData,
      fromFirestore: (snap: QueryDocumentSnapshot): T => snap.data() as T,
    });

    return new Observable<(DataType & Record<string, string>) | undefined>((observer) => {
      const colRef = collection(this.firestore, collectionPath).withConverter(
        passthroughConverter<DataType>(),
      );
      const q = query(colRef, limit(1));

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (snapshot.empty) {
            observer.next(undefined);
            return;
          }
          const docSnapshot = snapshot.docs[0];
          const data = docSnapshot.data(); // typed as DataType
          observer.next({...(data), [idField]: docSnapshot.id} as DataType & Record<string, string>);
        },
        (error) => {
          observer.error(error);
        },
      );

      return () => {
        unsubscribe();
      };
    }).pipe(shareReplay({bufferSize: 1, refCount: true}));
  }


}
